import { createContext, Fragment, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import type { LucideIcon } from 'lucide-react'

export interface MultiButtonItem {
  id: string
  icon: LucideIcon
  label: string
  onClick?: () => void
  disabled?: boolean
  /** Extra classes applied to this item's button (e.g. destructive text/hover color) */
  className?: string
}

export type MultiButtonVariant = 'default' | 'outline' | 'secondary' | 'ghost'
export type MultiButtonSize = 'sm' | 'md' | 'lg'

export interface MultiButtonProps {
  items: MultiButtonItem[]
  /**
   * Reference items used *only* for width calculation.
   * Pass this in table contexts so every row computes the same container width
   * regardless of per-row label variations (e.g. "Delete" vs "Protected").
   * Should contain the longest possible label for each slot.
   *
   * When inside a MultiButtonGroup, the group's shared max takes precedence.
   */
  syncWidthTo?: MultiButtonItem[]
  variant?: MultiButtonVariant
  size?: MultiButtonSize
  className?: string
}

// ─── MultiButtonGroup context ────────────────────────────────────────────────

interface MultiButtonGroupCtx {
  register: (id: string, maxLabelW: number) => void
  unregister: (id: string) => void
  sharedMaxLabelW: number
}

const MultiButtonGroupContext = createContext<MultiButtonGroupCtx | null>(null)

/**
 * Wraps a table or list of MultiButton rows and automatically synchronises
 * all contained MultiButton instances to the same width.  No manual
 * `syncWidthTo` props needed — any MultiButton inside this wrapper will
 * expand to the widest sibling automatically.
 */
export function MultiButtonGroup({ children }: { children: React.ReactNode }) {
  const [registry, setRegistry] = useState<Record<string, number>>({})

  const register = useCallback((id: string, maxLabelW: number) => {
    setRegistry(prev => {
      if (prev[id] === maxLabelW) return prev   // bail if unchanged → no re-render
      return { ...prev, [id]: maxLabelW }
    })
  }, [])

  const unregister = useCallback((id: string) => {
    setRegistry(prev => {
      if (!(id in prev)) return prev
      const rest = Object.fromEntries(
        Object.entries(prev).filter(([key]) => key !== id)
      )
      return rest
    })
  }, [])

  const sharedMaxLabelW = Object.keys(registry).length > 0
    ? Math.max(...Object.values(registry))
    : 0

  const ctx = useMemo(
    () => ({ register, unregister, sharedMaxLabelW }),
    [register, unregister, sharedMaxLabelW],
  )

  return (
    <MultiButtonGroupContext.Provider value={ctx}>
      {children}
    </MultiButtonGroupContext.Provider>
  )
}

// ─── Size config ──────────────────────────────────────────────────────────────

const SIZE_CONFIG = {
  sm: { heightCn: 'h-8',  cellW: 32, charW: 6.5, padEnd: 10, textCn: 'text-xs', iconCn: 'size-3.5' },
  md: { heightCn: 'h-9',  cellW: 36, charW: 7.5, padEnd: 12, textCn: 'text-sm', iconCn: 'size-4'   },
  lg: { heightCn: 'h-10', cellW: 40, charW: 8.0, padEnd: 14, textCn: 'text-sm', iconCn: 'size-[18px]' },
} as const

/** Rough pixel width of a label string at a given size. */
function estimateLabelW(label: string, charW: number, padEnd: number): number {
  return Math.ceil(label.length * charW) + padEnd
}

// ─── MultiButton ──────────────────────────────────────────────────────────────

export function MultiButton({
  items,
  syncWidthTo,
  variant = 'default',
  size = 'md',
  className,
}: MultiButtonProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  // Touch: first tap expands (like hover), second tap on same item fires the action.
  const [touchExpandedId, setTouchExpandedId] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const cfg = SIZE_CONFIG[size]
  const group = useContext(MultiButtonGroupContext)
  const instanceId = useId()
  const groupRegister = group?.register
  const groupUnregister = group?.unregister

  // Collapse when the user taps anywhere outside this component.
  useEffect(() => {
    if (!touchExpandedId) return
    const collapse = (e: TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setTouchExpandedId(null)
      }
    }
    document.addEventListener('touchstart', collapse)
    return () => document.removeEventListener('touchstart', collapse)
  }, [touchExpandedId])

  // Width calculation uses syncWidthTo when provided so all rows in a table column
  // share the same container width regardless of per-row label variation.
  const widthItems = syncWidthTo ?? items
  const ownMaxLabelW = widthItems.length > 0
    ? Math.max(...widthItems.map(item => estimateLabelW(item.label, cfg.charW, cfg.padEnd)))
    : 0

  // Register with group so siblings sync to the same max width.
  // register/unregister are stable useCallback refs, so this only re-runs when
  // instanceId or ownMaxLabelW changes (i.e. labels change between renders).
  useLayoutEffect(() => {
    if (!groupRegister || !groupUnregister) return
    groupRegister(instanceId, ownMaxLabelW)
    return () => groupUnregister(instanceId)
  }, [groupRegister, groupUnregister, instanceId, ownMaxLabelW])

  // Inside a group: use shared max, falling back to own max on the first render
  // before the registration flush so there is never a zero-width flash.
  // Outside a group: use own max (or syncWidthTo reference if provided).
  const maxLabelW = group
    ? Math.max(group.sharedMaxLabelW, ownMaxLabelW)
    : ownMaxLabelW

  const N = items.length
  const dividerPx = N > 1 ? N - 1 : 0

  // Container is permanently fixed at this width — rest size = max possible size
  const containerW = N * cfg.cellW + maxLabelW + dividerPx

  // At rest each item gets an equal share of the total item space
  const restItemW = N > 0 ? (containerW - dividerPx) / N : cfg.cellW

  // The effective "active" slot — mouse hover takes priority, touch expansion as fallback.
  const activeId = hoveredId ?? touchExpandedId

  // translateX that centers the icon within restItemW; resets to 0 when any slot is active.
  const iconOffset = activeId === null ? (restItemW - cfg.cellW) / 2 : 0

  const containerCn = {
    default:   'bg-primary text-primary-foreground shadow transition-shadow hover:shadow-md',
    outline:   'border border-input bg-background text-foreground shadow-sm',
    secondary: 'bg-secondary text-secondary-foreground shadow-sm',
    ghost:     'border border-border/50 text-foreground',
  }[variant]

  const dividerCn = {
    default:   'bg-primary-foreground/20',
    outline:   'bg-black/10 dark:bg-white/10',
    secondary: 'bg-black/10 dark:bg-white/10',
    ghost:     'bg-black/10 dark:bg-white/10',
  }[variant]

  const itemHoverCn = {
    default:   'hover:bg-primary-foreground/10 active:bg-primary-foreground/20',
    outline:   'hover:bg-muted/60 active:bg-muted',
    secondary: 'hover:bg-secondary-foreground/10 active:bg-secondary-foreground/20',
    ghost:     'hover:bg-muted/60 active:bg-muted',
  }[variant]

  return (
    <div
      ref={containerRef}
      style={{ width: `${containerW}px` }}
      className={cn(
        'inline-flex items-stretch rounded-full overflow-hidden',
        cfg.heightCn,
        containerCn,
        className,
      )}
    >
      {items.map((item, index) => {
        const Icon = item.icon
        const isActive = activeId === item.id
        const isCompressed = activeId !== null && !isActive

        // Active: full expansion. Compressed: icon-only. Rest: equal share.
        const itemW = isActive
          ? cfg.cellW + maxLabelW
          : isCompressed
          ? cfg.cellW
          : restItemW

        return (
          <Fragment key={item.id}>
            {index > 0 && (
              <div
                aria-hidden
                className={cn(
                  'w-px self-stretch shrink-0 transition-opacity duration-200',
                  dividerCn,
                  activeId !== null ? 'opacity-0' : 'opacity-100',
                )}
              />
            )}
            <button
              type="button"
              aria-label={item.label}
              disabled={item.disabled}
              onClick={() => {
                // After a touch-expand, collapse on action (mouse clicks pass straight through).
                if (touchExpandedId === item.id) setTouchExpandedId(null)
                item.onClick?.()
              }}
              onTouchStart={(e) => {
                // First tap: expand this slot without firing the action.
                // Second tap on the same slot: let the click event through naturally.
                if (touchExpandedId !== item.id) {
                  e.preventDefault()
                  setTouchExpandedId(item.id)
                }
              }}
              onMouseEnter={() => setHoveredId(item.id)}
              onMouseLeave={() => setHoveredId(null)}
              onFocus={() => setHoveredId(item.id)}
              onBlur={() => setHoveredId(null)}
              style={{ width: `${itemW}px`, cursor: 'pointer' }}
              className={cn(
                'flex items-center overflow-hidden shrink-0',
                'transition-[width] duration-200 ease-out',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring',
                'disabled:pointer-events-none disabled:opacity-50',
                itemHoverCn,
                item.className,
              )}
            >
              {/* Icon — translateX centers it at rest, slides flush-left on any hover */}
              <span
                style={{
                  width: `${cfg.cellW}px`,
                  transform: `translateX(${iconOffset}px)`,
                }}
                className="flex items-center justify-center shrink-0 transition-transform duration-200 ease-out"
              >
                <Icon className={cfg.iconCn} />
              </span>
              {/* Label — fades in after expansion begins */}
              <span
                className={cn(
                  'shrink-0 whitespace-nowrap font-medium leading-none',
                  cfg.textCn,
                  'pr-3',
                  'transition-opacity ease-out',
                  isActive
                    ? 'opacity-100 duration-150 delay-75'
                    : 'opacity-0 duration-100 delay-0',
                )}
              >
                {item.label}
              </span>
            </button>
          </Fragment>
        )
      })}
    </div>
  )
}

// ─── CompactMultiButton ───────────────────────────────────────────────────────

export interface CompactMultiButtonProps {
  items: MultiButtonItem[]
  /** The ID of the item shown at rest (collapsed state). */
  selectedId: string
  /**
   * When true, expands into an icon-only row — no labels ever appear and
   * individual buttons don't expand on hover. The touch interaction also
   * skips the label-reveal step (first tap fires the action directly).
   */
  iconOnly?: boolean
  /**
   * Reference items used *only* for width calculation when expanded.
   * Same semantics as MultiButton's syncWidthTo.
   */
  syncWidthTo?: MultiButtonItem[]
  variant?: MultiButtonVariant
  size?: MultiButtonSize
  className?: string
}

/**
 * A compact variant of MultiButton that shows only the selected item at rest.
 * Expands into the standard multi-button layout on hover (desktop) or first tap (mobile).
 */
export function CompactMultiButton({
  items,
  selectedId,
  iconOnly = false,
  syncWidthTo,
  variant = 'default',
  size = 'md',
  className,
}: CompactMultiButtonProps) {
  const [mouseExpanded, setMouseExpanded] = useState(false)
  const [touchExpanded, setTouchExpanded] = useState(false)
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [touchExpandedId, setTouchExpandedId] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const cfg = SIZE_CONFIG[size]

  const isExpanded = mouseExpanded || touchExpanded

  // Collapse when tapping outside (touch devices)
  useEffect(() => {
    if (!touchExpanded) return
    const collapse = (e: TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setTouchExpanded(false)
        setTouchExpandedId(null)
      }
    }
    document.addEventListener('touchstart', collapse)
    return () => document.removeEventListener('touchstart', collapse)
  }, [touchExpanded])

  const widthItems = syncWidthTo ?? items
  // iconOnly: no label space reserved — expanded width is purely icon cells + dividers
  const maxLabelW = iconOnly
    ? 0
    : widthItems.length > 0
    ? Math.max(...widthItems.map(item => estimateLabelW(item.label, cfg.charW, cfg.padEnd)))
    : 0

  const N = items.length
  const dividerPx = N > 1 ? N - 1 : 0
  const expandedW = N * cfg.cellW + maxLabelW + dividerPx
  const restW = cfg.cellW
  const containerW = isExpanded ? expandedW : restW

  // iconOnly: no individual item expansion; standard: track hover/touch for label reveal
  const activeId = iconOnly ? null : (isExpanded ? (hoveredId ?? touchExpandedId) : null)

  // Equal-share width for each item in the expanded rest state (mirrors MultiButton)
  const expandedRestItemW = N > 0 ? (expandedW - dividerPx) / N : cfg.cellW

  // Icon centering offset: only active in the expanded rest state
  const iconOffset = isExpanded && activeId === null
    ? (expandedRestItemW - cfg.cellW) / 2
    : 0

  const containerCn = {
    default:   'bg-primary text-primary-foreground shadow transition-shadow hover:shadow-md',
    outline:   'border border-input bg-background text-foreground shadow-sm',
    secondary: 'bg-secondary text-secondary-foreground shadow-sm',
    ghost:     'border border-border/50 text-foreground',
  }[variant]

  const dividerCn = {
    default:   'bg-primary-foreground/20',
    outline:   'bg-black/10 dark:bg-white/10',
    secondary: 'bg-black/10 dark:bg-white/10',
    ghost:     'bg-black/10 dark:bg-white/10',
  }[variant]

  const itemHoverCn = {
    default:   'hover:bg-primary-foreground/10 active:bg-primary-foreground/20',
    outline:   'hover:bg-muted/60 active:bg-muted',
    secondary: 'hover:bg-secondary-foreground/10 active:bg-secondary-foreground/20',
    ghost:     'hover:bg-muted/60 active:bg-muted',
  }[variant]

  return (
    <div
      ref={containerRef}
      style={{ width: `${containerW}px` }}
      onMouseEnter={() => setMouseExpanded(true)}
      onMouseLeave={() => { setMouseExpanded(false); setHoveredId(null) }}
      className={cn(
        'inline-flex items-stretch rounded-full overflow-hidden',
        cfg.heightCn,
        containerCn,
        'transition-[width] duration-200 ease-out',
        className,
      )}
    >
      {items.map((item, index) => {
        const Icon = item.icon
        const isSelected = item.id === selectedId
        const isActive = activeId === item.id
        const isCompressed = activeId !== null && !isActive

        // Collapsed: only the selected item occupies space; others collapse to 0.
        // Expanded: same expansion logic as MultiButton.
        const itemW = !isExpanded
          ? (isSelected ? cfg.cellW : 0)
          : isActive
          ? cfg.cellW + maxLabelW
          : isCompressed
          ? cfg.cellW
          : expandedRestItemW

        return (
          <Fragment key={item.id}>
            {index > 0 && (
              <div
                aria-hidden
                // Divider width animates alongside the container so zero-width
                // items before the selected one don't offset its position.
                style={{ width: isExpanded ? '1px' : '0px' }}
                className={cn(
                  'self-stretch shrink-0',
                  'transition-[width,opacity] duration-200',
                  dividerCn,
                  isExpanded && activeId === null ? 'opacity-100' : 'opacity-0',
                )}
              />
            )}
            <button
              type="button"
              aria-label={item.label}
              disabled={item.disabled}
              onClick={() => {
                if (touchExpandedId === item.id) setTouchExpandedId(null)
                item.onClick?.()
              }}
              onTouchStart={(e) => {
                if (!isExpanded) {
                  // First tap on collapsed button: expand the group, don't fire action.
                  e.preventDefault()
                  setTouchExpanded(true)
                } else if (!iconOnly && touchExpandedId !== item.id) {
                  // First tap on an item in expanded state: reveal its label (skipped in iconOnly).
                  e.preventDefault()
                  setTouchExpandedId(item.id)
                }
                // iconOnly+expanded or touchExpandedId===item.id → let click fire naturally.
              }}
              onMouseEnter={() => { if (isExpanded && !iconOnly) setHoveredId(item.id) }}
              onMouseLeave={() => { if (!iconOnly) setHoveredId(null) }}
              onFocus={() => { if (isExpanded && !iconOnly) setHoveredId(item.id) }}
              onBlur={() => { if (!iconOnly) setHoveredId(null) }}
              style={{ width: `${itemW}px`, cursor: 'pointer' }}
              className={cn(
                'flex items-center overflow-hidden shrink-0',
                'transition-[width] duration-200 ease-out',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring',
                'disabled:pointer-events-none disabled:opacity-50',
                itemHoverCn,
                item.className,
              )}
            >
              {/* Icon — centered at rest, flush-left on any hover */}
              <span
                style={{
                  width: `${cfg.cellW}px`,
                  transform: `translateX(${iconOffset}px)`,
                }}
                className="flex items-center justify-center shrink-0 transition-transform duration-200 ease-out"
              >
                <Icon className={cfg.iconCn} />
              </span>
              {/* Label — fades in after the item expands; omitted entirely in iconOnly mode */}
              {!iconOnly && (
                <span
                  className={cn(
                    'shrink-0 whitespace-nowrap font-medium leading-none',
                    cfg.textCn,
                    'pr-3',
                    'transition-opacity ease-out',
                    isActive
                      ? 'opacity-100 duration-150 delay-75'
                      : 'opacity-0 duration-100 delay-0',
                  )}
                >
                  {item.label}
                </span>
              )}
            </button>
          </Fragment>
        )
      })}
    </div>
  )
}
